/**
 * IN SHORT: This is the main orchestrator that you interact with to send and receive secure data. 
 * It combines Signing (JWS) and Encryption (JWE) into a single "Nested" package.
 *
 * Nested JWT pipeline: Sign → Encrypt (request) and Decrypt → Verify (response).
 *
 * RFC 7519 §6.2 defines nested JWTs as JWS tokens carried inside a JWE.
 * The inner content type is signalled via `cty: 'JWT'` in the JWE header.
 *
 * REQUEST FLOW:
 *   payload
 *     → signJws(clientPrivateKey)  → compact_jws
 *     → encryptJwe(serverEncKey)   → compact_jwe   (sent to server)
 *
 * RESPONSE FLOW:
 *   compact_jwe   (received from server)
 *     → decryptJwe(clientDecryptKey) → compact_jws
 *     → verifyJws(serverVerifyKey)   → payload
 *
 * SECURITY INVARIANTS:
 * 1. Encryption wraps the signature — server cannot strip the signature and
 *    re-sign with their own key (confused deputy).
 * 2. The outer JWE provides confidentiality + integrity of the JWS header and
 *    payload, hiding even the signing key ID from passive observers.
 * 3. Algorithms are pinned at the module boundary — see types/index.ts.
 */

import type { ClientKeyPair, ServerPublicKeys, AppPayload, ResolvedConfig } from '../types/index.js';
import { signJws, verifyJws } from './jws.js';
import { encryptJwe, decryptJwe } from './jwe.js';
import { generateJti, generateNonce, JtiCache } from '../utils/nonce.js';
import { nowSeconds } from '../utils/time.js';
import { JwtSdkError, JwtSdkErrorCode, ReplayAttackError } from '../errors/index.js';

export interface BuildRequestTokenOptions {
  payload: Record<string, unknown>;
  clientKeyPair: ClientKeyPair;
  serverPublicKeys: ServerPublicKeys;
  config: Pick<ResolvedConfig, 'tokenTtlSeconds' | 'issuer' | 'audience' | 'enableReplayPrevention'>;
  additionalClaims?: Record<string, unknown>;
}

export interface BuildRequestTokenResult {
  token: string;
  nonce: string;
}

export interface ParseResponseTokenOptions {
  token: string;
  clientKeyPair: ClientKeyPair;
  serverPublicKeys: ServerPublicKeys;
  config: Pick<
    ResolvedConfig,
    'issuer' | 'audience' | 'serverIssuer' | 'clockSkewSeconds' | 'enableReplayPrevention'
  >;
  jtiCache: JtiCache;
  expectedNonce?: string;
}

/**
 * Build a nested JWT for an outbound request.
 * Returns the compact JWE token and the nonce embedded in it.
 * Pass the nonce to parseResponseToken as `expectedNonce` to bind the response
 * to this specific request and prevent response substitution.
 */
export async function buildRequestToken(opts: BuildRequestTokenOptions): Promise<BuildRequestTokenResult> {
  const { payload, clientKeyPair, serverPublicKeys, config, additionalClaims } = opts;

  const jti = generateJti();
  const nonce = generateNonce();
  const expiresAt = nowSeconds() + config.tokenTtlSeconds;

  // Inner layer: JWS signed with client's private signing key.
  // additionalClaims are merged first so that core payload fields always win.
  let innerJws: string;
  try {
    innerJws = await signJws({
      payload: { ...additionalClaims, ...payload },
      privateKey: clientKeyPair.signingKey,
      keyId: clientKeyPair.keyId,
      expiresAt,
      ...(config.issuer !== undefined && { issuer: config.issuer }),
      ...(config.audience !== undefined && { audience: config.audience }),
      jti,
      nonce,
    });
  } catch (err) {
    if (err instanceof JwtSdkError) throw err;
    throw JwtSdkError.wrap(JwtSdkErrorCode.SIGNING_FAILED, 'Failed to sign request payload', err);
  }

  // Outer layer: JWE encrypted with server's public encryption key.
  let token: string;
  try {
    token = await encryptJwe({
      plaintext: innerJws,
      recipientPublicKey: serverPublicKeys.encryptionKey,
      ...(serverPublicKeys.keyId !== undefined && { recipientKeyId: serverPublicKeys.keyId }),
      isNestedJwt: true,
    });
  } catch (err) {
    if (err instanceof JwtSdkError) throw err;
    throw JwtSdkError.wrap(JwtSdkErrorCode.ENCRYPTION_FAILED, 'Failed to encrypt request token', err);
  }

  return { token, nonce };
}

/**
 * Parse and validate a nested JWT response from the server.
 * Returns the verified application payload.
 */
export async function parseResponseToken<T extends Record<string, unknown> = Record<string, unknown>>(
  opts: ParseResponseTokenOptions,
): Promise<AppPayload<T>> {
  const { token, clientKeyPair, serverPublicKeys, config, jtiCache } = opts;

  // Outer layer: decrypt with client's private decryption key.
  let innerJws: string;
  try {
    const decrypted = await decryptJwe({
      token,
      privateKey: clientKeyPair.decryptionKey,
    });

    if (decrypted.protectedHeader.cty !== 'JWT') {
      throw new JwtSdkError(
        JwtSdkErrorCode.INVALID_TOKEN,
        `Expected cty=JWT in JWE header, got cty=${decrypted.protectedHeader.cty}`,
      );
    }
    innerJws = decrypted.plaintext;
  } catch (err) {
    if (err instanceof JwtSdkError) throw err;
    throw JwtSdkError.wrap(JwtSdkErrorCode.DECRYPTION_FAILED, 'Failed to decrypt response token', err);
  }

  // Inner layer: verify with server's public signing key.
  let verifyResult: Awaited<ReturnType<typeof verifyJws<T>>>;
  try {
    // Direction flip for responses:
    // - Outbound request:  iss=client, aud=server  (config.issuer / config.audience)
    // - Inbound response:  iss=server, aud=client
    //
    // Expected `aud` on a server response is the client's OWN identifier
    // (config.issuer). Expected `iss` is config.serverIssuer if set —
    // otherwise issuer validation is skipped (not recommended in production).
    verifyResult = await verifyJws<T>({
      token: innerJws,
      publicKey: serverPublicKeys.verificationKey,
      ...(config.issuer !== undefined && { audience: config.issuer }),
      ...(config.serverIssuer !== undefined && { issuer: config.serverIssuer }),
      clockSkewSeconds: config.clockSkewSeconds,
    });
  } catch (err) {
    if (err instanceof JwtSdkError) throw err;
    throw JwtSdkError.wrap(JwtSdkErrorCode.VERIFICATION_FAILED, 'Failed to verify response signature', err);
  }

  const claimedPayload = verifyResult.payload as AppPayload<T>;

  // Nonce binding: verify the response carries the nonce from the originating request.
  if (opts.expectedNonce !== undefined && claimedPayload.nonce !== opts.expectedNonce) {
    throw new JwtSdkError(JwtSdkErrorCode.INVALID_TOKEN, 'Response nonce does not match request nonce');
  }

  // Replay prevention on responses.
  if (config.enableReplayPrevention && claimedPayload.jti) {
    const firstSeen = jtiCache.checkAndRecord(claimedPayload.jti, claimedPayload.exp);
    if (!firstSeen) throw new ReplayAttackError();
  }

  return claimedPayload;
}
